import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import metadataQueryKeys from "shared/services/metadataQueryKeys";
import { Outlet } from "react-router-dom";
import { useSession } from "app/hooks/useSession";
import PortalFrameComponent, { PortalFrameProps } from "./PortalFrame";
import { GATE_VUE_RUNTIME_QUERY_KEY, fetchGateVueRuntimeSource } from "modules/gate/services/gateVendorAssets";

const GATE_PORTAL_FRAME_CACHE_LIMIT_PROPERTY = "prm.gate.portal_frame_cache_limit";
const DEFAULT_GATE_PORTAL_FRAME_CACHE_LIMIT = 3;
const MIN_GATE_PORTAL_FRAME_CACHE_LIMIT = 1;
const MAX_GATE_PORTAL_FRAME_CACHE_LIMIT = 10;

type PortalFrameCacheEntry = {
    node: ReactNode;
    lastUsedAt: number;
};

interface GatePortalCacheContextValue {
    registerActive: (cacheKey: string, props: PortalFrameProps) => void;
    clearActive: () => void;
    generation: number;
}

export const GatePortalCacheContext = createContext<GatePortalCacheContextValue | null>(null);

export const useGatePortalCache = () => useContext(GatePortalCacheContext);

interface PortalFrameCacheProps {
    children?: ReactNode;
}

export const resolveGatePortalFrameCacheLimit = (value: unknown) => {
    const normalizedValue = String(value ?? "").trim();

    if (!/^\d+$/.test(normalizedValue)) {
        return DEFAULT_GATE_PORTAL_FRAME_CACHE_LIMIT;
    }

    const parsedValue = Number.parseInt(normalizedValue, 10);

    if (!Number.isFinite(parsedValue)) {
        return DEFAULT_GATE_PORTAL_FRAME_CACHE_LIMIT;
    }

    return Math.min(
        MAX_GATE_PORTAL_FRAME_CACHE_LIMIT,
        Math.max(MIN_GATE_PORTAL_FRAME_CACHE_LIMIT, parsedValue)
    );
};

const useGatePortalFrameCacheLimit = () => {
    const propertyQuery = useQuery({
        queryKey: metadataQueryKeys.property(GATE_PORTAL_FRAME_CACHE_LIMIT_PROPERTY),
        queryFn: () => new RecordManagement("sys_properties")
            .where("name", GATE_PORTAL_FRAME_CACHE_LIMIT_PROPERTY)
            .first(),
        staleTime: 1000 * 60 * 5,
        retry: false,
    });

    return resolveGatePortalFrameCacheLimit((propertyQuery.data as any)?.value);
};

const evictLeastRecentlyUsed = (entries: Map<string, PortalFrameCacheEntry>, activeKey: string, limit: number) => {
    while (entries.size > limit) {
        let oldestKey: string | undefined;
        let oldestTimestamp = Number.POSITIVE_INFINITY;

        entries.forEach((entry, key) => {
            if (key === activeKey) return;
            if (entry.lastUsedAt < oldestTimestamp) {
                oldestTimestamp = entry.lastUsedAt;
                oldestKey = key;
            }
        });

        if (!oldestKey) return;
        entries.delete(oldestKey);
    }
};

export const PortalFrameCache = ({ children }: PortalFrameCacheProps) => {
    const entriesRef = useRef<Map<string, PortalFrameCacheEntry>>(new Map());
    const previousSessionIdRef = useRef<string | undefined>();
    const sessionInitializedRef = useRef(false);
    const [activeKey, setActiveKey] = useState<string | null>(null);
    const [generation, setGeneration] = useState(0);
    const [, setRevision] = useState(0);
    const { data: sessionUser } = useSession();
    const sessionUserId = sessionUser?.id;
    const cacheLimit = useGatePortalFrameCacheLimit();
    const queryClient = useQueryClient();

    useEffect(() => {
        // Warms the shared query cache as soon as the user enters Gate routing, so by the time
        // any individual PortalFrame needs it, the fetch is already in flight or done.
        queryClient.prefetchQuery({
            queryKey: GATE_VUE_RUNTIME_QUERY_KEY,
            queryFn: fetchGateVueRuntimeSource,
            staleTime: Infinity,
        });
    }, [queryClient]);

    const bumpRevision = useCallback(() => {
        setRevision((current) => current + 1);
    }, []);

    const clearActive = useCallback(() => {
        setActiveKey(null);
    }, []);

    const registerActive = useCallback((cacheKey: string, props: PortalFrameProps) => {
        const entries = entriesRef.current;
        entries.set(cacheKey, {
            lastUsedAt: Date.now(),
            node: <PortalFrameComponent key={cacheKey} {...props} />,
        });
        evictLeastRecentlyUsed(entries, cacheKey, cacheLimit);
        setActiveKey(cacheKey);
        bumpRevision();
    }, [bumpRevision, cacheLimit]);

    useEffect(() => {
        if (!activeKey) return;

        evictLeastRecentlyUsed(entriesRef.current, activeKey, cacheLimit);
        bumpRevision();
    }, [activeKey, bumpRevision, cacheLimit]);

    useEffect(() => {
        if (!sessionInitializedRef.current) {
            previousSessionIdRef.current = sessionUserId;
            sessionInitializedRef.current = true;
            return;
        }

        if (previousSessionIdRef.current === sessionUserId) return;

        entriesRef.current.clear();
        previousSessionIdRef.current = sessionUserId;
        setActiveKey(null);
        setGeneration((current) => current + 1);
        bumpRevision();
    }, [bumpRevision, sessionUserId]);

    const contextValue = useMemo<GatePortalCacheContextValue>(() => ({
        clearActive,
        generation,
        registerActive,
    }), [clearActive, generation, registerActive]);

    const entries = Array.from(entriesRef.current.entries());

    return (
        <GatePortalCacheContext.Provider value={contextValue}>
            {children ?? <Outlet />}
            {entries.map(([cacheKey, entry]) => (
                <div
                    key={cacheKey}
                    data-gate-frame-cache-key={cacheKey}
                    style={{
                        display: activeKey === cacheKey ? "block" : "none",
                        width: "100%",
                        height: "100%",
                    }}
                >
                    {entry.node}
                </div>
            ))}
        </GatePortalCacheContext.Provider>
    );
};

export default PortalFrameCache;
